import ObjectModelBuilderModel from "../tracked/objectModelBuilderModel";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import GloballySelected from "../../../formability/object_model_builder/store/modules/selected";
import {ObjectModelPropertyCapability} from "./objectModelPropertyCapability";
import ObjectModelPropertyCapabilityManager from "./objectModelPropertyCapabilityManager";
import {ObjectModelPropertyRootType} from "../../enums/objectModelPropertyType";
import ObjectModel from "../objectModel";
import Guid from "../../helpers/guid";
import ObjectModelProperty from "./objectModelProperty";
import {RelationType} from "../relationType";
import {ObjectHelper} from "../../helpers/objectHelper";
import {Hash} from "../../interfaces/hashInterfaces";
import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
import ObjectModelConditionalFields, {ObjectModelTriggerConditionalField} from "~/models/objectModels/objectModelConditionalFields";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";

export default class LinkedRelationMappedProperty extends ObjectModelBuilderModel implements IConditionalRuleContainer{
  property_client_id: string;
  root_model_client_id: string;
  default_property: boolean;
  default_property_type: number;
  linked_property: ObjectModelProperty;
  deep_linked_property: ObjectModelProperty;
  object_model_properties: Hash<ObjectModelProperty> = {};
  mapped_property_type: number;
  filter_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet>;
  linked_property_has_filter: boolean;
  root_model: {id: string, client_id: string, title: string};
  _destroy: string;
  //Used for security masking
  linked_property_cache: ObjectModelProperty


  static repo: IRepo<LinkedRelationMappedProperty> = new Repo<LinkedRelationMappedProperty>('linkedRelationMappedProperties', LinkedRelationMappedProperty);

  constructor() {
    super();
    for (const definition of ObjectModelConditionalFields.AGGREGATE_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  initialize(root: ObjectModel, parent: ObjectModel | ObjectModelProperty = root, json)  {
    this.rootModel = root;
    this.parent = parent;
    this.defineProperty("property_client_id", json.property_client_id);
    this.defineProperty("root_model_client_id", json.root_model_client_id);
    this.defineProperty("default_property", json.default_property);
    this.defineProperty("default_property_type", json.default_property_type);
    this.defineProperty('linked_property_has_filter', false);
    this.defineProperty("client_id", Guid.create());
    return this
  }
  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: ObjectModelTriggerConditionalField): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    return ObjectModel.repo.find(this.root_model_client_id).objectModelPropertiesArray;
  }
  addToRepo(): void {
    LinkedRelationMappedProperty.repo.add(this);
  }

  get referenceId(){
    if (this.default_property){
      return this.default_property_type
    }else{
      return this.property_client_id
    }
  }

  static get rootName(): string {
    return 'linked_relation_mapped_properties';
  }

  get rootName(): string {
    return 'linked_relation_mapped_properties';
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType} } {
    const models = {
      'linked_property_cache': {type: ObjectModelProperty, relationType: RelationType.singleObject},
    };

    for (const definition of  ObjectModelConditionalFields.AGGREGATE_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  can(duty: ObjectModelPropertyCapability): boolean {
    return ObjectModelPropertyCapabilityManager.can(ObjectModelPropertyRootType.MappedProperty, duty);
  }

  get selector(): typeof GloballySelected['linkedRelationMappedProperty'] {
    return GloballySelected.linkedRelationMappedProperty;
  }

  get isSelectable(): boolean {
    return this.can(this.modelCapabilities.selectable);
  }

  select(): void {
    if (this.isSelectable) {
      this.selector.set(this);
    }
  }

  get modelCapabilities() {
    return ObjectModelPropertyCapability;
  }

  get objectModelPropertiesArray(): ObjectModelProperty[] {
    return ObjectHelper.toArray(this.object_model_properties);
  }

  get objectModel(): ObjectModel {
    if (this.parent) {
      if (this.parent instanceof ObjectModel) {
        return this.parent
      } else {
        return this.parent.objectModel
      }
    }
  }

  get linkedProperty(): ObjectModelProperty {
    return ObjectModelProperty.repo.findBy(item => item.client_id === this.property_client_id)
  }

  get relatedObjectModel(): ObjectModel {
    return ObjectModel.repo.find(this.root_model_client_id)
  }

  // Eitan: We are adding the linked_property after so that it can display data while not overriding its parent
  afterLoadFromJson() {
    super.afterLoadFromJson();
    ObjectModelProperty.repo.findOrCreate(this.property_client_id).loadFromJson(this.linked_property)
    if (this.deep_linked_property) {
      ObjectModelProperty.repo.findOrCreate(this.deep_linked_property.client_id).loadFromJson(this.deep_linked_property)
    }
  }
}